import { observer } from 'mobx-react';
import { Button, Input, InputType, View, ViewHeader, InputCalendar, InputDropdown, InputErrors } from '../../../../core/components/Index';
import useAsyncEffect from 'use-async-effect';
import { useNavigate } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import DeathReportViewModel from './DeathReportViewModel';
import StateList from '../../../../assets/data/States.json';
import Helper, { formatDate } from '../../../../core/services/Helper';
import ReCAPTCHA from "react-google-recaptcha";

const DeathReportView = observer(() => {
  const vm = Appl.Services.get<DeathReportViewModel>('DeathReportViewModel');
  vm.navigate = useNavigate();
  useAsyncEffect(async () => {
    await vm.initAsync();
  }, []);

  return (
    <>
      <View
        id="DeathReportView"
        pageTitle={vm.pageTitle}
        showMenuBar={false}
      >
        <ViewHeader title={vm.pageTitle} description={vm.pageDescription} helpPageId="" backUrl="" />
        {vm.stepStatus === 'New' &&
          <div className="space-y-[36px] mt-[24px]" id="formContainer">
            <h2 className="text-22px font-semibold mb-6 text-black">About the Deceased</h2>
            <div className="grid md:grid-cols-3 gap-4 mt-4 mb-6">
              <div>
                <Input
                  id="mbrFirstName"
                  value={vm.model.mbrFirstName}
                  inputType={InputType.Text}
                  onChange={(e) => {
                    vm.onInputChange(e, 'mbrFirstName', vm.model);
                  }}
                  label="First Name"
                  required={true}
                  maxLength={100}
                />
              </div>
              <div>
                <Input
                  id="mbrLastName"
                  value={vm.model.mbrLastName}
                  inputType={InputType.Text}
                  onChange={(e) => {
                    vm.onInputChange(e, 'mbrLastName', vm.model);
                  }}
                  label="Last Name"
                  required={true}
                  maxLength={100}
                />
              </div>
              <div>
                <label htmlFor="mbrSsn" className='font-bold text-15px text-black mb-1 block'>
                  SSN (Last 4 Digits)<span style={{ color: 'red' }}> * </span>
                </label>
                <div className='flex flex-wrap'>
                  <label className='text-15px text-black mt-3 block basis-2/12'>XXX-XX-</label>
                  <div className='basis-10/12'>
                    <Input
                      id="mbrSsn"
                      value={vm.model?.mbrSsn}
                      inputType={InputType.Text}
                      onChange={(e) => vm.onInputChange(e, 'mbrSsn', vm.model)}
                      style={{ width: '120px' }}
                      maxLength={4}
                      required={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-4 mb-6">
              <div>
                <InputCalendar
                  id="mbrBirthDate"
                  value={vm.model.mbrBirthDate!}
                  label="Date of Birth"
                  showHint={true}
                  onChange={(e) => {
                    vm.onInputChange(e, 'mbrBirthDate', vm.model);
                  }}
                  required={true}
                  maxDate={new Date()}
                />
              </div>
              <div>
                <InputCalendar
                  id="mbrDeathDate"
                  value={vm.model.mbrDeathDate!}
                  label="Date of Death"
                  showHint={true}
                  onChange={(e) => {
                    vm.onInputChange(e, 'mbrDeathDate', vm.model);
                  }}
                  required={true}
                  maxDate={new Date()}
                />
              </div>
            </div>
            <hr />
            <h2 className="text-22px font-semibold mb-6 text-black">About You</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 mb-6">
              <div>
                <Input
                  id="reqFirstName"
                  value={vm.model.reqFirstName}
                  inputType={InputType.Text}
                  onChange={(e) => {
                    vm.onInputChange(e, 'reqFirstName', vm.model);
                  }}
                  label="First Name"
                  required={true}
                  maxLength={100}
                />
              </div>
              <div>
                <Input
                  id="reqLastName"
                  value={vm.model.reqLastName}
                  inputType={InputType.Text}
                  onChange={(e) => {
                    vm.onInputChange(e, 'reqLastName', vm.model);
                  }}
                  label="Last Name"
                  required={true}
                  maxLength={100}
                />
              </div>
              <div>
                <InputDropdown
                  id="mbrRelationId"
                  value={vm.model?.mbrRelationId}
                  optionValue="key"
                  optionLabel="value"
                  options={vm.memberRelationList}
                  label="Relationship to the Deceased"
                  placeholder='Select'
                  style={{ width: '375px' }}
                  className="w-full"
                  required={true}
                  onChange={(e) => vm.onInputChange(e, 'mbrRelationId', vm.model)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 mb-6">
              <div>
                <Input
                  id="reqEmail"
                  value={vm.model.reqEmail}
                  inputType={InputType.Email}
                  onChange={(e) => {
                    vm.onInputChange(e, 'reqEmail', vm.model);
                  }}
                  label="Email Address"
                  required={true}
                  maxLength={100}
                />
              </div>
              <div className='flex gap-2'>
                <div>
                  <Input
                    id="reqPhone"
                    label="Phone Number"
                    value={vm.model.reqPhone}
                    inputType={InputType.Phone}
                    onChange={(e) => vm.onInputChange(e, 'reqPhone', vm.model)}
                    required={true}
                  />
                </div>
                <div>
                  <InputDropdown
                    id="reqPhoneType"
                    value={vm.model?.reqPhoneType}
                    options={vm.phoneTypeList}
                    optionValue="key"
                    optionLabel="value"
                    label="Type"
                    placeholder='Select'
                    className="w-full"
                    required={false}
                    onChange={(e) => vm.onInputChange(e, 'reqPhoneType', vm.model)}
                  />
                </div>
              </div>
              <div>
                <InputDropdown
                  id="mbrContactPrefId"
                  value={vm.model?.mbrContactPrefId}
                  options={vm.contactPrefList}
                  optionValue="key"
                  optionLabel="value"
                  label="Preferred Method of Contact"
                  placeholder='Select'
                  style={{ width: '375px' }}
                  className="w-full"
                  required={false}
                  onChange={(e) => vm.onInputChange(e, 'mbrContactPrefId', vm.model)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 mb-6">
              <div>
                <Input
                  id="reqAddressLine1"
                  value={vm.model.reqAddressLine1}
                  inputType={InputType.Text}
                  onChange={(e) => {
                    vm.onInputChange(e, 'reqAddressLine1', vm.model);
                  }}
                  label="Address"
                  required={false}
                  maxLength={100}
                />
              </div>

              {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 mb-6"> */}
              <div>
                <Input
                  id="reqCity"
                  value={vm.model.reqCity}
                  inputType={InputType.Text}
                  onChange={(e) => {
                    vm.onInputChange(e, 'reqCity', vm.model);
                  }}
                  label="City"
                  required={false}
                  maxLength={100}
                />
              </div>
              <div className='flex gap-4'>
                <div>
                  <InputDropdown
                    id="reqState"
                    value={vm.model?.reqState}
                    options={StateList}
                    optionValue="value"
                    optionLabel="value"
                    label="State"
                    placeholder='Select'
                    className="w-full"
                    required={false}
                    onChange={(e) => vm.onInputChange(e, 'reqState', vm.model)}
                  />
                </div>
                <div className='flex-shrink-0'>
                  <Input
                    id="reqZipCode"
                    value={vm.model?.reqZipCode}
                    inputType={InputType.Text}
                    onChange={(e) => vm.onInputChange(e, 'reqZipCode', vm.model)}
                    label="Zip Code"
                    maxLength={5}
                    required={false}
                  />
                </div>
              </div>
              {/* </div> */}

            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 mb-6">
              <div></div>
              <div>
                <ReCAPTCHA id='captcha' sitekey={Appl.Setting.CaptchaSiteKey} onChange={(token: any) => vm.model.captchaToken = token} />
                {/* <GoogleReCaptchaProvider reCaptchaKey={Appl.Setting.CaptchaSiteKey} >
                  <GoogleReCaptcha onVerify={(token: any) => vm.model.captchaToken = token} refreshReCaptcha={vm.model.captchaToken === undefined} />
                </GoogleReCaptchaProvider> */}
                <InputErrors errors={Appl.Error?.items} viewId="captcha" />
              </div>
            </div>
            <hr />
          </div>
        }
        {vm.stepStatus === 'Summary' &&
          <div className="space-y-[36px] mt-4">
            <h2 className="text-22px font-semibold mb-6 text-black">About the Deceased</h2>
            <div>
              <p className='mb-2'>Name: <span className='font-semibold'>{vm.model.mbrFirstName} {vm.model.mbrLastName}</span></p>
              <p className='mb-2'>Date of Birth: <span className='font-semibold'>{formatDate(vm.model.mbrBirthDate)}</span></p>
              <p className='mb-2'>Date of Death: <span className='font-semibold'>{formatDate(vm.model.mbrDeathDate)}</span></p>
              <p className='mb-2'>SSN (Last 4 Digits): <span className='font-semibold'>{vm.model.mbrSsn}</span></p>
              <Button severity='warning' rounded outlined title='Edit Deceased Info' onClick={async () => { await vm.onEditInfoAsync() }} />
            </div>
            <hr />
            <h2 className="text-22px font-semibold mb-6 text-black">About You</h2>
            <div>
              <p className='mb-2'>Name: <span className='font-semibold'>{vm.model.reqFirstName} {vm.model.reqLastName}</span></p>
              <p className='mb-2'>Relationship to Deceased: <span className='font-semibold'>{vm.memberRelationList.find(o => o.key === vm.model.mbrRelationId && o.key !== '')?.value}</span></p>
              <p className='mb-2'>Email Address: <span className='font-semibold'>{vm.model.reqEmail}</span></p>
              <p className='mb-2'>Phone Number: <span className='font-semibold'>{Helper.formatPhone(vm.model.reqPhone!)}</span></p>
              <p className='mb-2'>Preferred Method of Contact: <span className='font-semibold'>{vm.contactPrefList.find(o => (o.key === vm.model.mbrContactPrefId && o.key !== ''))?.value}</span></p>
              <p className='mb-2'>Address: <span className='font-semibold'>{vm.getFormattedAddress()}</span></p>
              <Button severity='warning' rounded outlined title='Edit Your Info' onClick={async () => { await vm.onEditInfoAsync() }} />
            </div>
            <hr />
          </div>

        }
        {vm.stepStatus === 'Confirmation' &&
          <div className="space-y-6 mt-4">
            <h2 className="text-22px font-semibold mb-6 text-black">Thank You for Your Submission</h2>
            <div>
              <p className='mb-4'>Your death notification has been successfully submitted.</p>
              <p className='mb-2 font-semibold'>Your Submission ID is {vm.submissionConfirmId}.</p>
              <p className='mb-4'>Please save this number for your records. You will need it if you need to reference your submission in future communications.</p>
              <p className='mb-4'>You will receive an email confirmation shortly with the details of your submission. A SCERS representative will review the information and contact you regarding next steps.</p>
              <p className='mb-4'>When you are ready, you may safely close this browser window.</p>
              <p>If you have any questions or need additional assistance, please contact us at (916) 874-9119 or email sacretire@saccounty.gov.</p>
            </div>
          </div>
        }

        {vm.stepStatus === 'New' && <div className="flex items-center justify-center gap-x-6 my-[36px]">
          <Button title="Continue" rounded
            onClick={async () => await vm.saveAsync()}
            disabled={!vm.mandaoryFieldsFilled}
          />
          <Button title="Cancel" rounded severity="secondary" onClick={() => vm.navigate(-1)} />
        </div>
        }
        {vm.stepStatus === 'Summary' && <div className="flex items-center justify-center gap-x-6 my-[36px]">
          <Button title="Submit" rounded onClick={async () => await vm.saveAsync()} />
          <Button title="Cancel" rounded severity="secondary" onClick={async () => { await vm.onEditInfoAsync() }} />
        </div>
        }
      </View >
    </>
  );
});

export default DeathReportView;
